import { ControlIcon, EmptyState, Grid, GridRow } from '@bp/ui-components';
import classNames from 'classnames';
import { observer } from 'mobx-react-lite';
import { FC } from 'react';
import { JSX } from 'react/jsx-runtime';
import { useTranslation } from 'react-i18next';
import { ConstraintsType, GenericWarning } from '../../../utils/timetable/types';
import { useTimetableStore } from '../TimetableProvider';
import { CardControlItem } from './CardControlItem';

type CardControlProps = {
  className?: string | undefined;
};

export const CardControl: FC<CardControlProps> = observer(({ className }) => {
  const { t } = useTranslation();
  const pinboardStore = useTimetableStore();
  const result = pinboardStore.getControlModeResult();

  const hasAvailability = result?.availabilityWarnings && result.availabilityWarnings.length !== 0;
  const hasCondition = result?.conditionWarnings && result.conditionWarnings.length !== 0;
  const hasDependency = result?.dependencyWarnings && result.dependencyWarnings.length !== 0;

  const createConditionWarningItems = () => {
    const conditionWarningsByType = new Map<keyof ConstraintsType, GenericWarning[]>();

    result?.conditionWarnings?.forEach((warning) => {
      if (!conditionWarningsByType.has(warning.type)) {
        conditionWarningsByType.set(warning.type, [warning]);
      } else {
        const w = conditionWarningsByType.get(warning.type) ?? [];
        conditionWarningsByType.set(warning.type, [...w, warning]);
      }
    });

    const conditionWarningItems: JSX.Element[] = [];
    conditionWarningsByType.forEach((warnings, key) => {
      const genericWarnings = warnings.reduce((prev, curr) => {
        const involvedClasses = [...(prev.involvedClasses ?? []), ...(curr.involvedClasses ?? [])];
        return { ...prev, involvedClasses };
      });
      conditionWarningItems.push(<CardControlItem key={key} type='condition' genericWarning={genericWarnings} />);
    });

    return conditionWarningItems;
  };

  return (
    <Grid className={classNames('has-scrollbar', className)} style={{ height: 'fit-content' }}>
      {!hasAvailability && !hasCondition && !hasDependency && (
        <EmptyState
          size='small'
          subtitle={t('pinboard.control.noWarnings')}
          icon={<ControlIcon />}
          iconColor='var(--color-success)'
        />
      )}
      <GridRow
        direction='column'
        spacingTop={!hasAvailability ? 'none' : 'm'}
        spacingBottom={!hasAvailability ? 'none' : 'm'}
      >
        {hasAvailability &&
          result?.availabilityWarnings?.map((warning, index) => {
            return <CardControlItem key={index} type='availability' availabilityWarning={warning} />;
          })}
      </GridRow>
      <GridRow
        direction='column'
        spacingTop={!hasCondition || !hasAvailability ? 'none' : 'm'}
        spacingBottom={!hasCondition || !hasAvailability ? 'none' : 'm'}
      >
        {hasCondition && createConditionWarningItems()}
      </GridRow>
      <GridRow
        direction='column'
        spacingTop={!hasDependency ? 'none' : 'm'}
        spacingBottom={!hasDependency ? 'none' : 'm'}
      >
        {hasDependency &&
          result?.dependencyWarnings?.map((warning, index) => {
            return <CardControlItem key={index} type='dependency' genericWarning={warning} />;
          })}
      </GridRow>
    </Grid>
  );
});
